'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { ProductLogoMediumPlaces } from '@/components/Logo';
import { Text } from '@/components/Typography';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { ledgerColumn } from '../-shared/-components/ledgerColumn';
import { LedgerContent } from '../-shared/-components/LedgerContent';
import { LedgerDataTable } from '../-shared/-components/LedgerDataTable';
import { isForbiddenError, LedgerForbiddenState } from '../-shared/-components/LedgerForbiddenState';
import { LedgerHeader } from '../-shared/-components/LedgerHeader';
import { usePlaceLedger } from '../-shared/-query';
const LOOKUP_PATH = '/individual/portal/lookup';
export const Route = createFileRoute('/_individual/individual/portal/lookup/_ledger/places/')({
  component: PlacesLookupPage
});
type PlaceLedgerItem = NonNullable<ReturnType<typeof usePlaceLedger>['data']>['items'][number];
function PlacesLookupPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.lookupResources'
  });
  const {
    t: ct
  } = useTranslation(defaultNS, {
    keyPrefix: 'common.breadcrumbs'
  });
  const {
    t: th
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.home'
  });
  const navigate = Route.useNavigate();
  const {
    data,
    isPending,
    isError,
    error
  } = usePlaceLedger();
  if (isError && isForbiddenError(error)) {
    return <>
        <LedgerHeader breadcrumbs={[{
        label: ct('home'),
        link: {
          to: '/'
        }
      }, {
        label: th('lookup.title'),
        link: {
          to: LOOKUP_PATH
        }
      }, {
        label: t('places.title')
      }]} Logo={ProductLogoMediumPlaces} title={t('places.title')} />
        <LedgerContent>
          <LedgerForbiddenState onBack={() => void navigate({
          to: LOOKUP_PATH
        })} />
        </LedgerContent>
      </>;
  }
  if (isError) {
    throw error;
  }
  const columns = [ledgerColumn<PlaceLedgerItem>({
    id: 'name',
    header: t('places.headers.name'),
    cell: p => <Text variant="label">{p.name}</Text>
  }), ledgerColumn<PlaceLedgerItem>({
    id: 'code',
    header: t('places.headers.code'),
    cell: p => p.code ?? '—'
  }), ledgerColumn<PlaceLedgerItem>({
    id: 'status',
    header: t('places.headers.status'),
    cell: p => p.status
  })];
  return <>
      <LedgerHeader breadcrumbs={[{
      label: ct('home'),
      link: {
        to: '/'
      }
    }, {
      label: th('lookup.title'),
      link: {
        to: LOOKUP_PATH
      }
    }, {
      label: t('places.title')
    }]} Logo={ProductLogoMediumPlaces} title={t('places.title')} />
      <LedgerContent className="h-full">
        <LedgerDataTable<PlaceLedgerItem> fillHeight columns={columns} data={data?.items} isPending={isPending} getRowId={p => p.id} getSearchText={p => `${p.name} ${p.code ?? ''}`} searchPlaceholder={t('common.search')} emptyContent={<Text variant="p" className="text-neutral-muted-on-fill">
              {t('common.empty')}
            </Text>} />
      </LedgerContent>
    </>;
}